import type { Icon } from '@phosphor-icons/react'
import {
  AddressBookIcon,
  ArmchairIcon,
  CalendarDotsIcon,
  CompassIcon,
  ConfettiIcon,
  FilesIcon,
  ForkKnifeIcon,
  HouseSimpleIcon,
  ListChecksIcon,
  NotePencilIcon,
  SquaresFourIcon,
  SunIcon,
  UsersThreeIcon,
} from '@phosphor-icons/react'

import type { FeatureFlagKeyType } from '@scrambl/shared'
import { ModuleTypeEnum, PermissionActionEnum } from '@scrambl/shared'

export interface PersonalNavItem {
  href: string
  label: string
  icon: Icon
  featureFlag?: FeatureFlagKeyType
  permission?: {
    module: ModuleTypeEnum
    action: PermissionActionEnum
  }
  /**
   * Hrefs that also live under this item's path prefix but belong to another
   * nav item (e.g. /tasks/mine under /tasks). Used by the active-state check.
   */
  activeExcludes?: string[]
}

export interface PersonalNavSection {
  key: string
  /** Section label; the first section renders unlabelled. */
  title?: string
  items: PersonalNavItem[]
}

/**
 * The personal space navigation grammar. Deliberately NOT derived from
 * useNavigationItems: the personal shell is its own surface with its own
 * language (Family, Address Book, Vendors, Home Base) and a much smaller,
 * fixed set of destinations. Business modules never appear here — the
 * personal flag profile keeps them off, and this list simply doesn't know
 * about them.
 */
export const PERSONAL_NAV_SECTIONS: PersonalNavSection[] = [
  {
    key: 'you',
    items: [
      {
        href: '/today',
        label: 'Home',
        icon: HouseSimpleIcon,
      },
      {
        href: '/tasks/mine',
        label: 'My Day',
        icon: SunIcon,
        permission: {
          module: ModuleTypeEnum.TASK,
          action: PermissionActionEnum.READ,
        },
      },
      {
        href: '/calendar',
        label: 'Calendar',
        icon: CalendarDotsIcon,
        featureFlag: 'CALENDAR' as FeatureFlagKeyType,
      },
      {
        href: '/notes/mine',
        label: 'Notes',
        icon: NotePencilIcon,
        featureFlag: 'NOTES' as FeatureFlagKeyType,
        permission: {
          module: ModuleTypeEnum.NOTE,
          action: PermissionActionEnum.READ,
        },
      },
    ],
  },
  {
    key: 'house',
    title: 'Around the house',
    items: [
      {
        href: '/my-organization',
        label: 'Home Base',
        icon: ArmchairIcon,
        permission: {
          module: ModuleTypeEnum.ORGANIZATION,
          action: PermissionActionEnum.READ,
        },
      },
      {
        href: '/projects',
        label: 'Projects',
        icon: CompassIcon,
        featureFlag: 'PROJECTS' as FeatureFlagKeyType,
        permission: {
          module: ModuleTypeEnum.PROJECT,
          action: PermissionActionEnum.READ,
        },
      },
      {
        href: '/tasks',
        label: 'To-dos',
        icon: ListChecksIcon,
        featureFlag: 'TASKS' as FeatureFlagKeyType,
        permission: {
          module: ModuleTypeEnum.TASK,
          action: PermissionActionEnum.READ,
        },
        activeExcludes: ['/tasks/mine'],
      },
      {
        href: '/events',
        label: 'Events',
        icon: ConfettiIcon,
        featureFlag: 'EVENTS' as FeatureFlagKeyType,
        permission: {
          module: ModuleTypeEnum.EVENT,
          action: PermissionActionEnum.READ,
        },
      },
      {
        // Meals ride tasks (settings.mealSlot), so gate on TASK read too.
        href: '/meals',
        label: 'Meals',
        icon: ForkKnifeIcon,
        featureFlag: 'MEAL_PLANNER' as FeatureFlagKeyType,
        permission: {
          module: ModuleTypeEnum.TASK,
          action: PermissionActionEnum.READ,
        },
      },
      {
        href: '/documents',
        label: 'Documents',
        icon: FilesIcon,
        featureFlag: 'DOCUMENTS' as FeatureFlagKeyType,
        permission: {
          module: ModuleTypeEnum.DOCUMENT,
          action: PermissionActionEnum.READ,
        },
      },
      {
        href: '/collections',
        label: 'Collections',
        icon: SquaresFourIcon,
        featureFlag: 'COLLECTIONS' as FeatureFlagKeyType,
        permission: {
          module: ModuleTypeEnum.CUSTOM_COLLECTION,
          action: PermissionActionEnum.READ,
        },
      },
    ],
  },
  {
    key: 'people',
    title: 'People',
    items: [
      {
        href: '/team',
        label: 'Family',
        icon: UsersThreeIcon,
        featureFlag: 'TEAM' as FeatureFlagKeyType,
        permission: {
          module: ModuleTypeEnum.MEMBER,
          action: PermissionActionEnum.READ,
        },
      },
      {
        href: '/contacts',
        label: 'Address Book',
        icon: AddressBookIcon,
        featureFlag: 'CONTACTS' as FeatureFlagKeyType,
        permission: {
          module: ModuleTypeEnum.CONTACT,
          action: PermissionActionEnum.READ,
        },
      },
      // Vendors deliberately absent: retired for personal spaces (2026-07-17).
      // The Address Book absorbs household pros (plumber, electrician…).
    ],
  },
]
